import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAnalysisDocuments, generateProposal } from '../services/proposal';

export const ProposalCenter = () => {
  const { id } = useParams();
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('executive_report');

  const fetchDocuments = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const docs = await getAnalysisDocuments(id);
      setDocuments(docs);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [id]);

  const handleRegenerate = async () => {
    if (!id) return;
    try {
      setGenerating(true);
      await generateProposal(id);
      await fetchDocuments();
    } catch (error) {
      console.error("Failed to generate proposal:", error);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const downloadFile = (text, filename, type) => {
    const content = type === 'json' ? JSON.stringify({ content: text }, null, 2) : text;
    const blob = new Blob([content], { type: type === 'json' ? 'application/json' : 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-pulse">Loading Proposal Center...</div></div>;
  }

  const hasDocuments = documents && (documents.proposal || documents.executive_report || documents.assets.length > 0);

  const renderContent = () => {
    if (!documents) return null;

    let contentToDisplay = '';

    if (activeTab === 'executive_report' && documents.executive_report) {
      contentToDisplay = documents.executive_report.content;
    } else if (activeTab === 'proposal' && documents.proposal) {
      contentToDisplay = documents.proposal.content;
    } else {
      const asset = documents.assets.find(a => a.asset_type === activeTab);
      if (asset) contentToDisplay = asset.content;
    }

    if (!contentToDisplay) {
      return (
        <div className="text-gray-500 italic p-8 text-center border-2 border-dashed rounded-lg bg-gray-50">
          No document available for this section.
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-sm border p-6 mt-6">
        <div className="flex justify-end space-x-2 mb-4 border-b pb-4">
          <button onClick={() => copyToClipboard(contentToDisplay)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">Copy Text</button>
          <button onClick={() => downloadFile(contentToDisplay, `${activeTab}.md`, 'markdown')} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">Download MD</button>
          <button onClick={() => downloadFile(contentToDisplay, `${activeTab}.json`, 'json')} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">Download JSON</button>
        </div>
        <div className="prose max-w-none">
          <pre className="whitespace-pre-wrap font-sans text-gray-800 bg-gray-50 p-6 rounded-lg border">
            {contentToDisplay}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Proposal Center</h1>
          <p className="text-gray-500 mt-2">AI-generated business proposals and sales assets</p>
        </div>
        <button
          onClick={handleRegenerate}
          disabled={generating}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 flex items-center space-x-2"
        >
          {generating ? <span>Generating...</span> : <span>Regenerate Assets</span>}
        </button>
      </div>

      {!hasDocuments ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No documents found</h2>
          <p className="text-gray-500 mb-6">Click regenerate to create AI proposals based on the analysis.</p>
          <button onClick={handleRegenerate} className="px-6 py-2 bg-blue-600 text-white rounded-lg">Generate Now</button>
        </div>
      ) : (
        <>
          <div className="flex overflow-x-auto space-x-2 border-b border-gray-200 pb-2 custom-scrollbar">
            {[
              { id: 'executive_report', label: 'Executive Summary' },
              { id: 'proposal', label: 'Full Proposal' },
              { id: 'cold_email', label: 'Cold Email' },
              { id: 'linkedin', label: 'LinkedIn Message' },
              { id: 'meeting_notes', label: 'Meeting Notes' },
              { id: 'roi', label: 'ROI Estimate' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </>
      )}
    </div>
  );
};

export default ProposalCenter;
